import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { generateMTCPdf } from "../utils/generateMTC";
import "./ViewCertificate.css";

function ViewCertificate() {
  const navigate = useNavigate();
  const [mtcList, setMtcList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });
  const [filters, setFilters] = useState({
    valve_type: "",
    status: "",
    customer: "",
    certificate_number: "",
  });
  const [selectedMTC, setSelectedMTC] = useState(null);

  useEffect(() => {
    fetchMTCList();
  }, [filters]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: "", message: "" }), 3000);
  };

  const fetchMTCList = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.valve_type) params.valve_type = filters.valve_type;
      if (filters.status) params.status = filters.status;
      if (filters.customer) params.customer = filters.customer;
      if (filters.certificate_number) params.certificate_number = filters.certificate_number;

      const response = await axios.get("/mtc", { params });
      if (response.data && response.data.success) {
        setMtcList(response.data.data || []);
      } else {
        setMtcList([]);
      }
    } catch (error) {
      console.error("Failed to fetch MTC list:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to load MTC certificates";
      showNotification("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    fetchMTCList();
  };

  const handleClearFilters = () => {
    setFilters({
      valve_type: "",
      status: "",
      customer: "",
      certificate_number: "",
    });
    setTimeout(() => fetchMTCList(), 100);
  };

  const handleDownloadPdf = async (mtc) => {
    try {
      setLoading(true);

      // Helper function to format dates consistently (DD/MM/YYYY)
      const formatDate = (dateValue) => {
        if (!dateValue) return "";
        try {
          const str = String(dateValue).trim();
          const d = new Date(str);
          if (isNaN(d.getTime())) return str;
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yyyy = d.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        } catch (e) {
          return dateValue || "";
        }
      };

      // Fetch TCDS data if tcds_id is available
      let tcdsData = {};
      if (mtc.tcds_id) {
        try {
          console.log('Fetching TCDS data for ID:', mtc.tcds_id);
          // Try different endpoints based on valve type (API 600, API 6D, or standard)
          let endpoints = [`/tcds/${mtc.tcds_id}`];

          // Add API-specific endpoints based on valve type
          if (mtc.valve_type === 'gate' || mtc.valve_type === 'check') {
            endpoints.unshift(`/tcds/api-600/${mtc.tcds_id}`, `/tcds/api-6d/${mtc.tcds_id}`);
          } else {
            endpoints.unshift(`/tcds/api-600/${mtc.tcds_id}`, `/tcds/api-6d/${mtc.tcds_id}`);
          }

          // Try endpoints in order
          for (const endpoint of endpoints) {
            try {
              const tcdsResponse = await axios.get(endpoint);
              if (tcdsResponse.data && tcdsResponse.data.success) {
                tcdsData = tcdsResponse.data.data || {};
                console.log('TCDS Data fetched from:', endpoint, tcdsData);
                break;
              }
            } catch (err) {
              console.warn(`Failed to fetch from ${endpoint}:`, err.response?.status);
              continue;
            }
          }

          // If still no data, try sections endpoint
          if (!tcdsData || Object.keys(tcdsData).length === 0) {
            try {
              const altResponse = await axios.get(`/tcds/sections/${mtc.tcds_no}`);
              if (altResponse.data && altResponse.data.success && altResponse.data.data?.length > 0) {
                tcdsData = altResponse.data.data[0] || {};
                console.log('TCDS Data from sections endpoint:', tcdsData);
              }
            } catch (altErr) {
              console.warn('Failed to fetch from sections endpoint:', altErr.message);
            }
          }
        } catch (tcdsErr) {
          console.warn('Failed to fetch TCDS data:', tcdsErr.message);
        }
      } else {
        console.warn('No TCDS ID available in MTC record:', mtc.tcds_no);
      }

      // Parse item_description if it's a string
      let itemDescription = {};
      if (mtc.item_description) {
        itemDescription = typeof mtc.item_description === 'string'
          ? JSON.parse(mtc.item_description)
          : mtc.item_description;
      }

      // Parse heat_annexure_data if it's a string
      let heatAnnexureData = null;
      if (mtc.heat_annexure_data) {
        heatAnnexureData = typeof mtc.heat_annexure_data === 'string'
          ? JSON.parse(mtc.heat_annexure_data)
          : mtc.heat_annexure_data;
      }

      // Map item description fields from ViewCertificate format to MTC PDF format
      const itemDescFieldMapping = {
        body: 'body',
        Body: 'body',
        bonnet: 'bonnet',
        Bonnet: 'bonnet',
        wedge: 'wedge',
        Wedge: 'wedge',
        disc: 'disc',
        Disk: 'disc',
        seat_ring: 'seat_ring',
        'Seat Ring': 'seat_ring',
        seat: 'seat',
        Seat: 'seat',
        stem: 'stem',
        Stem: 'stem',
        gasket: 'gasket',
        Gasket: 'gasket',
        packing: 'packing',
        Packing: 'packing',
        bolting: 'bolting',
        Bolting: 'bolting',
        stud_nut: 'stud_nut',
        'Stud & Nut': 'stud_nut',
      };

      // Convert item description to PDF-friendly format
      const pdfItemDescription = {};
      Object.entries(itemDescription).forEach(([key, value]) => {
        const mappedKey = itemDescFieldMapping[key] || key;
        pdfItemDescription[mappedKey] = value;
      });

      // Prepare form data for PDF generation
      const formData = {
        tcds_no: mtc.tcds_no,
        tcds_date: formatDate(mtc.tcds_date),
        date: formatDate(mtc.date),
        customer: mtc.customer,
        project_name: mtc.project_name,
        po_no: mtc.po_no,
        po_date: formatDate(mtc.po_date),
        certificate_no: mtc.certificate_number,
        certificate_number: mtc.certificate_number,
        tag_no: mtc.tag_no,
        qap_no: mtc.qap_no,
        gad_no: mtc.gad_no,
        end_connection: mtc.end_connection,
        valve_type: mtc.valve_type,
        size: mtc.size,
        class: mtc.class,
        design_standard: mtc.design_standard || mtc.design_type,
        testing_std: mtc.testing_std || mtc.testing_standard,
        testing_standard: mtc.testing_standard || mtc.testing_std,
        test_pressure: mtc.test_pressure,
        testing_duration: mtc.testing_duration,
        test_duration: mtc.testing_duration,
        test_result: mtc.test_result,
        // Testing pressure fields (hydrostatic tests)
        test_pressure_back_seat: mtc.hydro_back_seat,
        test_pressure_body: mtc.hydro_body,
        body_hydrostatic: mtc.hydro_body,
        test_pressure_seat: mtc.hydro_seat,
        seat_hydrostatic: mtc.hydro_seat,
        test_pressure_pneumatic: mtc.pneumatic_seat,
        seat_pneumatic: mtc.pneumatic_seat,
        hydro_back_seat: mtc.hydro_back_seat,
        hydro_body: mtc.hydro_body,
        hydro_seat: mtc.hydro_seat,
        pneumatic_seat: mtc.pneumatic_seat,
        material_spec: mtc.material_spec,
        heat_no: mtc.heat_no,
        mtc_ref_no: mtc.mtc_ref_no,
        remarks: mtc.remarks,
        remark: mtc.remarks, // Map database 'remarks' column to form field 'remark' for PDF generation
        conformity_statement: mtc.conformity_statement,
        customer_item_code: mtc.customer_item_code,
        prepared_by: mtc.prepared_by,
        approved_by: mtc.approved_by,
        prepared_by_name: mtc.prepared_by_name,
        qac_officer: mtc.qac_officer,
        batch_no: mtc.batch_no || mtc.certificate_number,
        heat_annexure_qty: mtc.qty,
        // Manual entry fields
        oa_no: mtc.oa_no || "",
        oa_date: formatDate(mtc.oa_date),
        ga_drawing_no: mtc.ga_drawing_no || "",
        fail_position: mtc.fail_position || "",
        power_fail_position: mtc.power_fail_position || "",
        ipc_sr_no: mtc.ipc_sr_no || "",
        valve_batch_sr_no: mtc.valve_batch_sr_no || "",
        observation: mtc.observation || mtc.remarks || "",
        min_air_supply: mtc.min_air_supply || "",
        pressure_rating: mtc.pressure_rating || "",
        declaration_of_compliance: mtc.declaration_of_compliance || "",
        body_hydrostatic: mtc.body_hydrostatic || "",
        seat_hydrostatic: mtc.seat_hydrostatic || "",
        seat_pneumatic: mtc.seat_pneumatic || "",
        test_duration: mtc.test_duration || "",
        // Add material fields directly from database columns
        body_material: mtc.body_material,
        disc_material: mtc.disc_material,
        stem_material: mtc.stem_material,
        seat_material: mtc.seat_material,
        bonnet_material: mtc.bonnet_material,
        gasket_material: mtc.gasket_material,
        packing_material: mtc.packing_material,
        bolting_material: mtc.bolting_material,
        // Add fallback from item_description for backward compatibility
        ...itemDescription,
        // Heat Annexure flag - needed for conditional "Refer Annexure" display in PDF
        attach_heat_annexure: mtc.attach_heat_annexure,
      };

      // Generate PDF - pass heat annexure data if attached
      const heatData = mtc.attach_heat_annexure ? heatAnnexureData : null;

      // Fetch attachment images AND PDFs from heat annexure configurations
      let attachmentImages = [];
      let attachmentPdfs = [];
      if (heatData && heatData.length > 0) {
        try {
          // Get unique attachment IDs from heat annexure data
          const attachmentIds = [...new Set(heatData.map(row => row.attachmentId).filter(id => id))];
          console.log('Found attachment IDs:', attachmentIds);

          for (const attachmentId of attachmentIds) {
            try {
              const response = await axios.get(`/heat-annexure/${attachmentId}/attachment`, {
                responseType: 'blob',
                timeout: 30000
              });
              if (response.data && response.data.size > 0) {
                const mimeType = response.data.type || 'image/jpeg';

                if (mimeType === 'application/pdf') {
                  // Handle PDF attachment
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(response.data);
                  });
                  attachmentPdfs.push({
                    url: base64,
                    mimetype: mimeType
                  });
                } else {
                  // Handle image attachment
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(response.data);
                  });
                  attachmentImages.push({
                    url: `data:${mimeType};base64,${base64}`,
                    label: `Attachment: ${attachmentId}`
                  });
                }
              }
            } catch (imgErr) {
              console.warn('Failed to fetch attachment ID:', attachmentId, imgErr.message);
            }
          }
        } catch (err) {
          console.warn('Failed to fetch attachments:', err.message);
        }
      }
      console.log('Total image attachments:', attachmentImages.length);
      console.log('Total PDF attachments:', attachmentPdfs.length);

      generateMTCPdf(tcdsData, formData, mtc.valve_type, heatData, attachmentImages, attachmentPdfs);
      showNotification("success", "PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showNotification("error", "Failed to download PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (mtc) => {
    setSelectedMTC(selectedMTC?.id === mtc.id ? null : mtc);
  };

  const handleEditMTC = (mtc) => {
    navigate(`/mtc/edit/${mtc.id}`);
  };

  const handleDeleteMTC = async (mtc) => {
    if (!window.confirm(`Are you sure you want to delete certificate ${mtc.certificate_number}?`)) {
      return;
    }
    try {
      setLoading(true);
      const res = await axios.delete(`/mtc/${mtc.id}`);
      if (res.data.success) {
        showNotification("success", "Certificate deleted successfully");
        fetchMTCList();
      } else {
        showNotification("error", res.data.message || "Failed to delete certificate");
      }
    } catch (error) {
      console.error("Error deleting MTC:", error);
      const errMsg = error.response?.data?.message || error.message;
      showNotification("error", errMsg || "Failed to delete certificate");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const getValveTypeLabel = (type) => {
    const labels = {
      gate: "Gate Valve",
      globe: "Globe Valve",
      check: "Check Valve",
      ball: "Ball Valve",
      lsb: "LSB Valve",
      actuator: "Actuator",
      automation: "Automation",
    };
    return labels[type] || type;
  };

  return (
    <div className="view-certificate-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === "success" ? "✓" : "✕"}
          </div>
          <div className="notification-text">{notification.message}</div>
        </div>
      )}

      <div className="certificate-header">
        <h1 className="certificate-title">Print Certificate</h1>
        <p className="certificate-subtitle">View and download MTC certificates</p>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-field">
            <label>Certificate No.</label>
            <input
              type="text"
              name="certificate_number"
              value={filters.certificate_number}
              onChange={handleFilterChange}
              placeholder="Search by certificate no."
            />
          </div>
          <div className="filter-field">
            <label>Valve Type</label>
            <select
              name="valve_type"
              value={filters.valve_type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="gate">Gate Valve</option>
              <option value="globe">Globe Valve</option>
              <option value="check">Check Valve</option>
              <option value="ball">Ball Valve</option>
              <option value="lsb">LSB Valve</option>
              <option value="actuator">Actuator</option>
              <option value="automation">Automation</option>
            </select>
          </div>
          <div className="filter-field">
            <label>Customer</label>
            <input
              type="text"
              name="customer"
              value={filters.customer}
              onChange={handleFilterChange}
              placeholder="Search by customer"
            />
          </div>
        </div>
        <div className="filter-actions">
          <button className="btn-search" onClick={handleSearch}>
            Search
          </button>
          <button className="btn-clear" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* MTC List */}
      <div className="mtc-list-section">
        <div className="list-header">
          <h2>MTC Certificates ({mtcList.length})</h2>
        </div>

        {mtcList.length === 0 ? (
          <div className="no-data-message">
            <p>No MTC certificates found.</p>
            <p>Generate certificates from the Manage MTC section.</p>
          </div>
        ) : (
          <div className="mtc-table-wrapper">
            <table className="mtc-table">
              <thead>
                <tr>
                  <th>Certificate No.</th>
                  <th>TCDS No.</th>
                  <th>Valve Type</th>
                  <th>Customer</th>
                  <th>PO No.</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mtcList.map((mtc) => (
                  <React.Fragment key={mtc.id}>
                    <tr className={selectedMTC?.id === mtc.id ? "selected" : ""}>
                      <td className="certificate-no">{mtc.certificate_number}</td>
                      <td>{mtc.tcds_no || "-"}</td>
                      <td>
                        <span className="valve-type-badge">
                          {getValveTypeLabel(mtc.valve_type)}
                        </span>
                      </td>
                      <td>{mtc.customer || "-"}</td>
                      <td>{mtc.po_no || "-"}</td>
                      <td>{formatDate(mtc.date)}</td>
                      <td className="actions-cell">
                        <button
                          className="btn-action btn-view"
                          onClick={() => handleViewDetails(mtc)}
                          title="View Details"
                        >
                          {selectedMTC?.id === mtc.id ? "Hide" : "View"}
                        </button>
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleEditMTC(mtc)}
                          title="Edit Certificate"
                        >
                          Edit
                        </button>
                        <button
                          className="btn-action btn-download"
                          onClick={() => handleDownloadPdf(mtc)}
                          title="Download PDF"
                        >
                          Download
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteMTC(mtc)}
                          title="Delete Certificate"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {selectedMTC?.id === mtc.id && (
                      <tr className="details-row">
                        <td colSpan="7">
                          <div className="mtc-details">
                            <div className="details-grid">
                              <div className="detail-group">
                                <h4>Certificate Information</h4>
                                <p><strong>Certificate No:</strong> {mtc.certificate_number}</p>
                                <p><strong>TCDS No:</strong> {mtc.tcds_no || "-"}</p>
                                <p><strong>Date:</strong> {formatDate(mtc.date)}</p>
                                <p><strong>Sr. No:</strong> {mtc.sr_no || "-"}</p>
                              </div>
                              <div className="detail-group">
                                <h4>Customer Details</h4>
                                <p><strong>Customer:</strong> {mtc.customer || "-"}</p>
                                <p><strong>Project:</strong> {mtc.project_name || "-"}</p>
                                <p><strong>PO No:</strong> {mtc.po_no || "-"}</p>
                                <p><strong>PO Date:</strong> {formatDate(mtc.po_date)}</p>
                              </div>
                              <div className="detail-group">
                                <h4>Valve Specifications</h4>
                                <p><strong>Type:</strong> {getValveTypeLabel(mtc.valve_type)}</p>
                                <p><strong>Size:</strong> {mtc.size || "-"}</p>
                                <p><strong>Class:</strong> {mtc.class || "-"}</p>
                                <p><strong>End Connection:</strong> {mtc.end_connection || "-"}</p>
                                <p><strong>Design Std:</strong> {mtc.design_standard || "-"}</p>
                              </div>
                              <div className="detail-group">
                                <h4>Testing Details</h4>
                                <p><strong>Test Pressure:</strong> {mtc.test_pressure || "-"}</p>
                                <p><strong>Duration:</strong> {mtc.testing_duration || "-"}</p>
                                <p><strong>Result:</strong> <span className={`result-${(mtc.test_result || "").toLowerCase()}`}>{mtc.test_result || "-"}</span></p>
                              </div>
                              <div className="detail-group">
                                <h4>Material Components</h4>
                                {mtc.item_description && typeof mtc.item_description === 'object' && Object.entries(mtc.item_description).slice(0, 8).map(([key, value]) => (
                                  <p key={key}><strong>{key}:</strong> {value || "-"}</p>
                                ))}
                              </div>
                              <div className="detail-group">
                                <h4>Signatures</h4>
                                <p><strong>Prepared By:</strong> {mtc.prepared_by || "-"}</p>
                                <p><strong>Approved By:</strong> {mtc.approved_by || "-"}</p>
                                <p><strong>Created:</strong> {formatDate(mtc.created_at)}</p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewCertificate;
